import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { siteConfig } from "@/config/site";

export default function Navigation() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const scrollTo = (href) => {
    setOpen(false);
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <motion.header
      data-testid="site-nav"
      initial={{ y: -30, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        scrolled
          ? "bg-[#0d1220]/80 backdrop-blur-xl border-b border-white/10"
          : "bg-transparent"
      }`}
    >
      <div className="vs-container flex items-center justify-between h-[76px]">
        <a
          href="#top"
          data-testid="nav-logo"
          onClick={(e) => {
            e.preventDefault();
            window.scrollTo({ top: 0, behavior: "smooth" });
          }}
          className="flex items-center gap-3 group"
        >
          <img
            src={siteConfig.logo}
            alt="VERYSIGHT"
            className="h-10 md:h-11 w-auto object-contain"
            style={{ mixBlendMode: "screen" }}
          />
          <span className="hidden sm:inline text-[10px] tracking-[0.35em] text-white/60 font-body uppercase mt-1">
            VerySight
          </span>
        </a>

        <nav className="hidden lg:flex items-center gap-9">
          {siteConfig.nav.map((item) => (
            <button
              key={item.href}
              data-testid={`nav-link-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
              onClick={() => scrollTo(item.href)}
              className="text-[13px] tracking-wide text-white/70 hover:text-white transition-colors font-body"
            >
              {item.label}
            </button>
          ))}
        </nav>

        <div className="hidden lg:block">
          <button
            data-testid="nav-cta-book"
            onClick={() => scrollTo("#contact")}
            className="vs-btn-primary"
          >
            Book Free Consultation
          </button>
        </div>

        <button
          data-testid="nav-mobile-toggle"
          aria-label="Toggle menu"
          onClick={() => setOpen((v) => !v)}
          className="lg:hidden p-2 text-white/80 hover:text-white"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden overflow-hidden bg-[#0d1220]/95 backdrop-blur-xl border-t border-white/10"
          >
            <div className="vs-container py-6 flex flex-col gap-1">
              {siteConfig.nav.map((item) => (
                <button
                  key={item.href}
                  data-testid={`nav-mobile-link-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                  onClick={() => scrollTo(item.href)}
                  className="text-left py-3 text-white/80 font-body text-sm border-b border-white/5"
                >
                  {item.label}
                </button>
              ))}
              <button
                data-testid="nav-mobile-cta"
                onClick={() => scrollTo("#contact")}
                className="vs-btn-primary mt-6 justify-center"
              >
                Book Free Consultation
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
